import objectID from 'bson-objectid';
import { CATALOG } from '../../../utils/constellationCatalog';
import {
  filenameFor,
  renderConstellationFile,
  ensureScriptTag,
  removeScriptTag
} from '../../../utils/constellationInject';
import {
  SKY_TRANSFORMS_SOURCE,
  SKY_TRANSFORMS_FILENAME
} from '../../../utils/skyTransformsSource';
import {
  SKY_DRAW_SOURCE,
  SKY_DRAW_FILENAME
} from '../../../utils/skyDrawSource';
import { getHTMLFile } from '../reducers/files';
import {
  createFile,
  updateFileContent,
  deleteFile,
  ensureFolder
} from './files';
import { HELPERS_FOLDER } from './helpers';
import { setUnsavedChanges } from './ide';

/** Injected constellation data files live under this folder in the file tree. */
export const CONSTELLATIONS_FOLDER = 'constellations';

/** A ProjectFile node with generated content. */
function makeFile(name, content) {
  const id = objectID().toHexString();
  return { name, id, _id: id, content, children: [] };
}

/** A ProjectFile node for a freshly generated constellation file. */
function newConstellationFile(name) {
  return makeFile(
    filenameFor(name),
    renderConstellationFile(name, CATALOG[name])
  );
}

/**
 * Apply a set of selected constellations to the current sketch: create files +
 * script tags for newly-checked constellations, delete files + tags for unchecked
 * ones, refresh content for those still checked, and patch `index.html` once.
 *
 * @param {string[]} selectedNames constellation names that should be present
 */
export function injectConstellations(selectedNames) {
  return (dispatch, getState) => {
    const state = getState();
    const htmlFile = getHTMLFile(state.files);
    const selected = new Set(selectedNames);

    let html = htmlFile ? htmlFile.content : '';

    // The real parent-folder id of a file node (the folder whose `children` holds it),
    // so deletes remove it from the right folder — its `helpers`/`constellations` folder
    // now, or root for a legacy pre-folder sketch. Falls back to root.
    const rootId = (state.files.find((f) => f && f.name === 'root') || {}).id;
    const parentIdOf = (childId) =>
      (
        state.files.find(
          (f) => f && Array.isArray(f.children) && f.children.includes(childId)
        ) || { id: rootId }
      ).id;

    // Resolve each target folder at most once per run (memoized). Called lazily so a
    // pure-removal run never creates an empty folder.
    let constellationsFolderId;
    const constellationsFolder = () => {
      if (!constellationsFolderId) {
        constellationsFolderId = dispatch(ensureFolder(CONSTELLATIONS_FOLDER));
      }
      return constellationsFolderId;
    };
    let helpersFolderId;
    const helpersFolder = () => {
      if (!helpersFolderId) {
        helpersFolderId = dispatch(ensureFolder(HELPERS_FOLDER));
      }
      return helpersFolderId;
    };

    Object.keys(CATALOG).forEach((name) => {
      const filename = filenameFor(name);
      const src = `${CONSTELLATIONS_FOLDER}/${filename}`;
      const existing = state.files.find(
        (f) => f && f.fileType === 'file' && f.name === filename
      );

      if (selected.has(name)) {
        if (existing) {
          // Refresh content so catalog corrections propagate; tag already present.
          dispatch(
            updateFileContent(
              existing.id,
              renderConstellationFile(name, CATALOG[name])
            )
          );
        } else {
          dispatch(
            createFile(newConstellationFile(name), constellationsFolder())
          );
        }
        html = ensureScriptTag(html, src);
      } else if (existing) {
        dispatch(deleteFile(existing.id, parentIdOf(existing.id)));
        html = removeScriptTag(html, src);
      }
    });

    // Sky helpers ride along whenever any constellation is present: skyTransforms.js
    // (lockStarsToSky etc.) and skyDraw.js (drawConstellationLines etc.). Created once each
    // (student edits are preserved); removed when the last constellation goes. skyTransforms
    // is tagged before skyDraw so the math half loads first.
    const skyHelpers = [
      { name: SKY_TRANSFORMS_FILENAME, source: SKY_TRANSFORMS_SOURCE },
      { name: SKY_DRAW_FILENAME, source: SKY_DRAW_SOURCE }
    ];
    skyHelpers.forEach(({ name, source }) => {
      const src = `${HELPERS_FOLDER}/${name}`;
      const existing = state.files.find(
        (f) => f && f.fileType === 'file' && f.name === name
      );
      if (selected.size > 0) {
        if (!existing) {
          dispatch(createFile(makeFile(name, source), helpersFolder()));
        }
        html = ensureScriptTag(html, src);
      } else if (existing) {
        dispatch(deleteFile(existing.id, parentIdOf(existing.id)));
        html = removeScriptTag(html, src);
      }
    });

    if (htmlFile && html !== htmlFile.content) {
      dispatch(updateFileContent(htmlFile.id, html));
    }
    dispatch(setUnsavedChanges(true));
  };
}

export default injectConstellations;
